import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { AdminApi } from '../../services/api';
import { BOUTON_AUTH, CHAMP_AUTH } from './CadreAuth';
import { Wordmark } from '../../shared/ui';
import {
  abonnerChangementMotDePasse,
  changementMotDePasseExige,
  oublierChangementMotDePasse,
  signalerMotDePasseChange,
  verifierNouveauMotDePasse,
  LONGUEUR_MIN_MOT_DE_PASSE,
} from './motDePasseObligatoire';

/**
 * Écran plein « Choisissez votre mot de passe », par-dessus toute
 * l'application, tant que le serveur exige un nouveau mot de passe. À la
 * réussite, les nouveaux jetons sont enregistrés et le travail reprend.
 */
export const EcranMotDePasseObligatoire: React.FC = () => {
  const [visible, setVisible] = useState(changementMotDePasseExige());
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => abonnerChangementMotDePasse(setVisible), []);

  if (!visible) return null;

  const envoyer = async (e: React.FormEvent) => {
    e.preventDefault();
    const probleme = verifierNouveauMotDePasse(nouveau, confirmation, actuel);
    setErreur(probleme);
    if (probleme) return;
    setEnvoi(true);
    try {
      await AdminApi.changePassword(actuel, nouveau);
      setActuel(''); setNouveau(''); setConfirmation('');
      signalerMotDePasseChange();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Le mot de passe n\'a pas pu être changé.');
    } finally {
      setEnvoi(false);
    }
  };

  const seDeconnecter = async () => {
    oublierChangementMotDePasse();
    await AdminApi.logout();
    window.location.assign('/login');
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="titre-mdp" className="fixed inset-0 z-[200] overflow-y-auto bg-papier">
      <div className="min-h-full flex items-center justify-center px-4 py-8 sm:p-6">
        <div className="w-full max-w-sm">
          <div className="mb-10"><Wordmark /></div>
          <h1 id="titre-mdp" className="text-2xl font-extrabold tracking-tight text-encre">Choisissez votre mot de passe</h1>
          <p className="mt-1 text-graphite">Votre compte utilise un mot de passe provisoire. Choisissez-en un nouveau pour continuer.</p>

          {erreur && (
            <div role="alert" className="mt-6 p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" /><p>{erreur}</p>
            </div>
          )}

          <form onSubmit={envoyer} className="mt-8 space-y-5" noValidate>
            <div>
              <label htmlFor="mdp-actuel" className="block text-sm font-semibold text-encre mb-1.5">Mot de passe actuel</label>
              <input id="mdp-actuel" type="password" value={actuel} onChange={e => setActuel(e.target.value)} autoComplete="current-password" className={CHAMP_AUTH} />
            </div>
            <div>
              <label htmlFor="mdp-nouveau" className="block text-sm font-semibold text-encre mb-1.5">Nouveau mot de passe</label>
              <input id="mdp-nouveau" type="password" value={nouveau} onChange={e => setNouveau(e.target.value)} autoComplete="new-password" className={CHAMP_AUTH} />
              <p className="mt-1 text-xs text-graphite">Au moins {LONGUEUR_MIN_MOT_DE_PASSE} caractères.</p>
            </div>
            <div>
              <label htmlFor="mdp-confirmation" className="block text-sm font-semibold text-encre mb-1.5">Confirmation</label>
              <input id="mdp-confirmation" type="password" value={confirmation} onChange={e => setConfirmation(e.target.value)} autoComplete="new-password" className={CHAMP_AUTH} />
            </div>
            <button type="submit" disabled={envoi} className={BOUTON_AUTH}>
              {envoi ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Enregistrement</span></> : <span>Enregistrer et continuer</span>}
            </button>
          </form>

          <button type="button" onClick={seDeconnecter} className="mt-6 min-h-[40px] lg:min-h-0 text-sm text-graphite hover:text-encre underline underline-offset-2">
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

export default EcranMotDePasseObligatoire;
